import { useEffect, useState } from 'react';
import { geoCacheKey, type GeoBounds } from '../../lib/metaAdGeoPoints';
import { MAPS_API_KEY, getGeocoder } from './googleMaps';

// Re-export the shared cache-key helper + the bounds type (both live in the pure lib so
// the builders use them without a module→lib cycle). Kept exported here for callers/tests.
export { geoCacheKey };
export type { GeoBounds };

// Audience-builder — resolve a geo target's CENTRE coordinate. Zernio's geo search
// returns { id(key), name, type, path, audienceSize } with NO coordinates, and
// there's no Zernio "resolve geo key → coords" endpoint, so we resolve the centre
// ourselves via Google Geocoding.
//
// DELIBERATELY GENERIC + SWAPPABLE: this is the SINGLE seam for "geo item → centre".
// It takes any geo item (type + key + name + path), not just cities, so the same
// function serves places/neighborhoods in a later phase. The SOURCE can swap behind
// it: when Zernio adds coordinates to geo-search results, `resolveGeoCenter` just
// returns those authoritative coords (an early `item.latitude/longitude` short-circuit
// here) with NO change to the drawing code that calls it.

export type LatLng = { latitude: number; longitude: number };

// What `resolveGeoCenter` returns: the centre PLUS the two extra artifacts the SAME
// geocode response carries — the Google `placeId` (feeds Data-Driven Styling boundary
// highlighting for country/region) and the `bounds` (map-fit + bbox fallback). Point
// types use latitude/longitude only; area types additionally use placeId / bounds.
export type GeoResolution = {
  latitude: number;
  longitude: number;
  placeId: string | null;
  bounds: GeoBounds | null;
  // SAFETY (data-integrity): true when this centre was resolved by GEOCODING THE NAME
  // (no authoritative coords from the source, no disambiguating breadcrumb) — so it may
  // NOT match the place the Meta `key` actually targets at publish (same name, different
  // suburb or even country). The map must present an approximate centre as UNCONFIRMED,
  // never as a certain location. false = trustworthy (user-placed pin, future Zernio
  // coords, an unambiguous country, or a path-disambiguated geocode). Optional so legacy
  // callers/tests that build a resolution by hand default to trustworthy.
  approximate?: boolean;
};

export type GeoItem = {
  type: string; // 'city' | 'region' | 'place' | 'neighborhood' | 'country' | 'zip' | …
  key: string; // the platform geo key (the cache identity)
  name: string;
  // The search-result breadcrumb (path[0] = country label, e.g.
  // ['Australia','Queensland','Parkwood']) — used to disambiguate same-named localities.
  path?: string[] | null;
  // FUTURE: when the source (Zernio) provides coords, they'll arrive here and
  // short-circuit the geocode. Absent today.
  latitude?: number | null;
  longitude?: number | null;
};

// A geocoding function: query string → resolution (or null when unresolved). Injectable
// so the resolver is testable without the Google SDK.
export type GeocodeFn = (query: string) => Promise<GeoResolution | null>;

// Cache keyed by geo TYPE + KEY (coords don't change → geocode each once). Module-
// level + process-lifetime; resettable for tests.
const cache = new Map<string, GeoResolution | null>();

// Build the geocoder query from the name + parent breadcrumb, most-specific first
// (e.g. "Parkwood, Queensland, Australia"). Tolerant of a missing/short/oddly-shaped
// path. Pure.
export function buildGeocodeQuery(item: Pick<GeoItem, 'name' | 'path'>): string {
  const name = (item.name ?? '').trim();
  const path = Array.isArray(item.path)
    ? item.path.filter((p): p is string => typeof p === 'string' && p.trim() !== '').map((p) => p.trim())
    : [];
  // path is [country, …, locality]; reverse → most-specific first. If its first
  // element already equals the name, it IS the full chain; otherwise prepend name.
  const rev = [...path].reverse();
  const parts = rev.length > 0 && rev[0]!.toLowerCase() === name.toLowerCase() ? rev : [name, ...rev];
  return parts.filter(Boolean).join(', ');
}

// The default (Google) geocoder. Captures location + place_id + viewport/bounds from
// the SAME response (no extra calls). Returns null when no key (graceful) or on any
// failure — a caller never crashes on an unresolved geo.
const defaultGeocode: GeocodeFn = async (query) => {
  if (!MAPS_API_KEY || !query) return null;
  try {
    const geocoder = await getGeocoder();
    const res = await geocoder.geocode({ address: query });
    const top = res.results[0];
    const loc = top?.geometry?.location;
    if (!loc) return null;
    // Prefer the true area `bounds` (areas only); fall back to the always-present
    // `viewport`. NB this is the cheap bbox we draw for unsupported area types.
    const box = top!.geometry.bounds ?? top!.geometry.viewport ?? null;
    const bounds = box
      ? { north: box.getNorthEast().lat(), east: box.getNorthEast().lng(), south: box.getSouthWest().lat(), west: box.getSouthWest().lng() }
      : null;
    return { latitude: loc.lat(), longitude: loc.lng(), placeId: top!.place_id ?? null, bounds };
  } catch {
    return null;
  }
};

// Resolve a geo item. Cached by type+key (incl. cached misses, so an unresolvable geo
// isn't re-queried every render). `geocode` is injectable for tests.
export async function resolveGeoCenter(
  item: GeoItem,
  geocode: GeocodeFn = defaultGeocode,
): Promise<GeoResolution | null> {
  // SWAP POINT — authoritative coords from the source win, no geocode needed. These are
  // the real coordinates of the keyed place → CONFIRMED (approximate:false).
  if (typeof item.latitude === 'number' && typeof item.longitude === 'number') {
    return { latitude: item.latitude, longitude: item.longitude, placeId: null, bounds: null, approximate: false };
  }
  const ck = geoCacheKey(item);
  if (cache.has(ck)) return cache.get(ck)!;
  let result: GeoResolution | null = null;
  try {
    result = await geocode(buildGeocodeQuery(item));
  } catch {
    result = null;
  }
  if (result) {
    // Stamp the trust level (the resolver owns this, not the geocode fn). Only
    // AUTHORITATIVE COORDS (the short-circuit above) — or a COUNTRY, whose unambiguous
    // name is keyed by the ISO code we control — count as confirmed. A NAME-GEOCODE stays
    // approximate even now that Zernio returns a `path` breadcrumb: the breadcrumb
    // disambiguates the result for the human picking it, but the geocode is still a
    // separate name lookup that may not land on the place the opaque Meta key targets
    // (Parkwood wrong-country). The map-truth fix needs real coords (A) — still pending —
    // so the "verify before publishing" banner deliberately stays on for these.
    const confirmed = item.type === 'country';
    result = { ...result, approximate: !confirmed };
  }
  cache.set(ck, result);
  return result;
}

// Test-only: clear the module cache between cases.
export function resetGeoCenterCache(): void {
  cache.clear();
}

// Resolve the centres for a set of geo items (cities / places / neighborhoods — the
// map draws those that resolve). Re-runs when the set of (type,key) identities
// changes; resolveGeoCenter's cache makes repeats instant, so a radius edit (keys
// unchanged) never re-geocodes. Returns a { geoCacheKey → centre } map of only the
// SUCCESSFULLY-resolved items (graceful: an unresolved item is absent → not drawn).
export function useResolvedGeoCenters(items: GeoItem[]): Record<string, GeoResolution> {
  const keyList = items.map((i) => geoCacheKey(i)).join('|');
  const [centers, setCenters] = useState<Record<string, GeoResolution>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out: Record<string, GeoResolution> = {};
      for (const item of items) {
        const center = await resolveGeoCenter(item);
        if (center) out[geoCacheKey(item)] = center;
      }
      if (!cancelled) setCenters(out);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyList captures `items`
  }, [keyList]);

  return centers;
}
